import React from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import Badge from '@material-ui/core/Badge';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import CardMedia from '@material-ui/core/CardMedia';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell'; import grey from '@material-ui/core/colors/grey';
import { joinStringsWithSpace } from '../../../../shared/utils/strings';
import { UsersQuery_users_edges_node } from './__generated__/UsersQuery';
import AppAvatar from '../../../../shared/display/media/Avatar';
import AppFullScreenMedia from '../../../../shared/display/media/FullScreenMedia';
import { AuthContext } from '../../../../auth/store/auth-context';
import { MemberType, OrganizationMembershipRole, PlatformRole } from '../../../../__generated__/globalTypes';

const useStyles = makeStyles((theme) => ({
  row: {
    cursor: 'pointer',
    "&:hover": {
      backgroundColor: grey[100],
    },
  },
  avatar: {
    width: 40,
    marginRight: theme.spacing(2),
  },
  action: {
    width: 40,
  },
  fullScreen: {
    width: '100% !important',
    height: '100% !important',
    backgroundSize: 'contain',
    backgroundColor: 'black',
  },
  dot: {
    right: 5,
    bottom: 5,
    border: '1px solid white',
    width: 10,
    height: 10,
    borderRadius: 10,
  },
  disabled: {
    opacity: 0.5,
    background: '#f4f6f8',
  },
}));

export function userType(type: MemberType) {
  if (type === MemberType.UNKNOWN || type === MemberType.UNKNOWN_INT) {
    return null;
  }
  return type;
}

type UserRowProps = {
  user: UsersQuery_users_edges_node,
  hideEmail: boolean,
};

function UserRow({ user, hideEmail }: UserRowProps) {
  const classes = useStyles();
  const history = useHistory();
  const { state: stateAuth } = React.useContext(AuthContext);
  const [open, setOpen] = React.useState(false);
  const [isWorkspaceAdmin, setIsWorkspaceAdmin] = React.useState(false);
  const [primaryOrganization, setPrimaryOrganization] = React.useState('');
  const isPlatformAdmin = stateAuth.user?.platformRole === PlatformRole.ADMIN;

  const handleAvatarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  React.useEffect(
    () => {
      if (user) {
        let isWorkspaceAdminForUser = false;
        const userOrganizationMemberships = (user.organizationMemberships || []).map(item => item.organization.id).concat([user.primaryOrganization.id]);
        if (userOrganizationMemberships.length > 0 && stateAuth.workspaces) {
          isWorkspaceAdminForUser = stateAuth.workspaces.some(workspace => workspace.role === OrganizationMembershipRole.ADMIN && userOrganizationMemberships.indexOf(workspace.id) !== -1);
        }
        setIsWorkspaceAdmin(isWorkspaceAdminForUser);
        let userPrimaryOrganization = user.primaryOrganization.name;
        if (user.organizationMemberships.length === 0) {
          userPrimaryOrganization = '';
        } else if (!user.organizationMemberships.some(item => item.organization.name === userPrimaryOrganization)) {
          userPrimaryOrganization = user.organizationMemberships[0].organization.name;
        }
        setPrimaryOrganization(userPrimaryOrganization);
      }
    },
    [user]
  );

  return (
    <>
      <TableRow
        className={clsx(classes.row, {
          [classes.disabled]: !primaryOrganization,
        })}
        onClick={() => history.push({ pathname: `/users/${user.id}` })}
      >
        <TableCell className={classes.avatar} onClick={user.photo?.url ? handleAvatarClick : undefined}>
          <AppAvatar
            image={user.photo?.url}
            icon="account_circle"
          />
        </TableCell>
        <TableCell>{joinStringsWithSpace(user.firstName, user.lastName)}</TableCell>
        {!hideEmail && <TableCell>{user.email}</TableCell>}
        <TableCell>{primaryOrganization}</TableCell>
        <TableCell>{user.company}</TableCell>
        <TableCell>{user.position}</TableCell>
        <TableCell>{userType(user.memberType)}</TableCell>
        <TableCell className={classes.action}>
          <IconButton edge="end" size="small">
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      {open && (
        <AppFullScreenMedia title={joinStringsWithSpace(user.firstName, user.lastName)} onClose={handleClose}>
          <CardMedia image={user.photo?.url} className={classes.fullScreen} />
        </AppFullScreenMedia>
      )}
    </>
  );
}

export default UserRow;
